import * as React from "react";
import { Box } from "../../components/ui/box";
import { Icon, type IconName, type IconSource } from "../../components/ui/icon";
import { Toggle } from "../../components/ui/toggle";
import { useEntitySelector } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

export type EntitySelectorTabsProps = Omittable<{
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}>;

function EntitySelectorTabs({ children, ref }: EntitySelectorTabsProps) {
  return (
    // 6px from the popup's sides, on the edge the search well and the row
    // washes share (see entity-selector-layout).
    <Box ref={ref} direction="row" gap="xs" className="px-1.5" paddingY="sm">
      {children}
    </Box>
  );
}

EntitySelectorTabs.displayName = "EntitySelectorTabs";

export type EntitySelectorTabsListProps = {
  children: React.ReactNode;
};

// Spaced outline pills, single-select with no deselect: the active pool is
// always exactly one. Discrete Toggles in a gapped row (not ToggleGroup —
// its outline variant joins the pills into one segmented control).
function EntitySelectorTabsList({ children }: EntitySelectorTabsListProps) {
  return (
    <Box direction="row" align="center" gap="sm">
      {children}
    </Box>
  );
}

EntitySelectorTabsList.displayName = "EntitySelectorTabsList";

export type EntitySelectorTabsTriggerProps = Omittable<{
  value: string;
  icon?: IconSource;
  children?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}>;

function EntitySelectorTabsTrigger({
  value,
  icon,
  children,
  ref,
}: EntitySelectorTabsTriggerProps) {
  const { activePool, setActivePool, getPool } = useEntitySelector();
  const pool = getPool(value);

  const tabIcon = icon ?? pool?.icon;
  const label = children ?? pool?.label ?? value;

  return (
    <Toggle
      ref={ref}
      variant="outline"
      size="sm"
      pressed={activePool === value}
      onPressedChange={(pressed) => {
        if (pressed) setActivePool(value);
      }}
    >
      {tabIcon &&
        (typeof tabIcon === "string" ? (
          <Icon name={tabIcon as IconName} />
        ) : (
          <Icon as={tabIcon} />
        ))}
      {label}
    </Toggle>
  );
}

EntitySelectorTabsTrigger.displayName = "EntitySelectorTabsTrigger";

export type EntitySelectorAutoTabsProps = Omittable<{
  ref?: React.Ref<HTMLDivElement>;
}>;

function EntitySelectorAutoTabs({ ref }: EntitySelectorAutoTabsProps) {
  const { config } = useEntitySelector();

  if (config.pools.length <= 1) return null;

  return (
    <EntitySelectorTabs ref={ref}>
      <EntitySelectorTabsList>
        {config.pools.map((pool) => (
          <EntitySelectorTabsTrigger
            key={pool.id}
            value={pool.id}
            icon={pool.icon}
          >
            {pool.label}
          </EntitySelectorTabsTrigger>
        ))}
      </EntitySelectorTabsList>
    </EntitySelectorTabs>
  );
}

EntitySelectorAutoTabs.displayName = "EntitySelectorAutoTabs";

export {
  EntitySelectorTabs,
  EntitySelectorTabsList,
  EntitySelectorTabsTrigger,
  EntitySelectorAutoTabs,
};
